import * as React from 'react';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/providers/theme-provider';
import { useThemeMutation } from '@/hooks/use-theme-mutation';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
/**
 * Big-tile theme picker. Lives on the navbar so the choice is reachable
 * with one click from anywhere — same data path as the Profile select,
 * just a richer surface. Auto-closes when the user picks.
 */
export function ThemeDialog() {
    const { t } = useTranslation();
    const { theme, systemTheme } = useTheme();
    const { updateTheme } = useThemeMutation();
    const [open, setOpen] = React.useState(false);
    const options = [
        {
            value: 'light',
            label: t('light'),
            description: t('theme_dialog.light_desc', { defaultValue: 'A warm, paper-coloured backdrop.' }),
            icon: Sun,
        },
        {
            value: 'dark',
            label: t('dark'),
            description: t('theme_dialog.dark_desc', { defaultValue: 'Easy on the eyes after sundown.' }),
            icon: Moon,
        },
        {
            value: 'system',
            label: t('system'),
            description: t('theme_dialog.system_desc', { defaultValue: 'Follows your OS appearance.' }),
            icon: Monitor,
        },
    ];
    const handlePick = (next) => {
        updateTheme(next);
        setOpen(false);
    };
    // Choose the navbar icon to reflect the active theme so the button
    // itself reads as "currently light/dark/system". System resolves to
    // whichever the OS is reporting right now.
    const resolved = theme === 'system' ? (systemTheme ?? 'light') : theme;
    const TriggerIcon = theme === 'system' ? Monitor : resolved === 'dark' ? Moon : Sun;
    return (<Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="ghost" size="icon" aria-label={t('toggle_theme')} title={t('theme', { defaultValue: 'Theme' })}>
                    <TriggerIcon className="size-5"/>
                </Button>
            </DialogTrigger>

            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>{t('theme', { defaultValue: 'Theme' })}</DialogTitle>
                    <DialogDescription>
                        {t('theme_dialog.description', {
            defaultValue: 'Pick how the console should look. Applies immediately.',
        })}
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                    {options.map(({ value, label, description, icon: Icon }) => {
            const active = theme === value;
            return (<button key={value} type="button" onClick={() => handlePick(value)} className={cn('group relative flex flex-col items-start gap-2 rounded-lg border p-3 text-left transition-colors', 'hover:bg-accent hover:text-accent-foreground', active && 'border-primary bg-accent/40')}>
                                <div className="flex w-full items-center justify-between">
                                    <Icon className="size-5"/>
                                    {active && <Check className="size-4 text-primary"/>}
                                </div>
                                <div>
                                    <div className="text-sm font-medium">{label}</div>
                                    <div className="text-xs text-muted-foreground">{description}</div>
                                </div>
                            </button>);
        })}
                </div>
            </DialogContent>
        </Dialog>);
}
ThemeDialog.displayName = 'ThemeDialog';
